import { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, Image as ImageIcon, X } from 'lucide-react';
import clsx from 'clsx';
import { aiVideoApi, type AiGenJob, type CameraPresetDTO, type AiVideoQuota, type VideoModelDTO } from '@/api/aiVideo';
import { draftsApi } from '@/api/storyDrafts';
import { mediaApi } from '@/api/media';
import { walletApi } from '@/api/wallet';
import { useAuthStore } from '@/store/authStore';

const SUBUNIT = 100_000; // 1 SWIPA = 100 000 sub-units

interface Props {
  /** Вызывается после публикации или сохранения в черновики — обновить ленту/закрыть. */
  onPublished: () => void;
}

const ASPECTS: Array<{ value: '9:16' | '1:1' | '16:9'; label: string }> = [
  { value: '9:16', label: 'Вертикальное 9:16' },
  { value: '1:1', label: 'Квадрат 1:1' },
  { value: '16:9', label: 'Широкое 16:9' },
];

const POLL_MS = 4000;
const ACTIVE = (s?: AiGenJob['status']) => s === 'PENDING' || s === 'PROCESSING';

export default function AiVideoPanel({ onPublished }: Props) {
  const [presets, setPresets] = useState<CameraPresetDTO[]>([]);
  const [presetId, setPresetId] = useState<string>('none');
  const [models, setModels] = useState<VideoModelDTO[]>([]);
  // Без дефолта: пользователь сам выбирает движок (осознанная трата SWIPA на
  // конкретную модель). Пустая строка = ничего не выбрано → генерация заблокирована.
  const [modelId, setModelId] = useState<string>('');
  const [durationSec, setDurationSec] = useState<number>(6);
  const [aspect, setAspect] = useState<'9:16' | '1:1' | '16:9'>('9:16');
  const [sourceMediaId, setSourceMediaId] = useState<string | null>(null);
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [prompt, setPrompt] = useState('');
  const [quota, setQuota] = useState<AiVideoQuota | null>(null);
  const [job, setJob] = useState<AiGenJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Каталог пресетов + движков + квота при монтировании.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, m, q] = await Promise.all([aiVideoApi.presets(), aiVideoApi.models(), aiVideoApi.quota()]);
        if (!alive) return;
        setPresets(p);
        setModels(m);
        setQuota(q);
        if (p.length && !p.some((x) => x.id === presetId)) setPresetId(p[0].id);
        // Движок НЕ выбираем автоматически — это осознанный выбор пользователя.
      } catch {
        if (alive) setError('Не удалось загрузить движки');
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling статуса активной задачи.
  useEffect(() => {
    if (!job || !ACTIVE(job.status)) return;
    pollRef.current = setTimeout(async () => {
      try {
        const next = await aiVideoApi.get(job.id);
        setJob(next);
        if (!ACTIVE(next.status)) {
          aiVideoApi.quota().then(setQuota).catch(() => {});
        }
      } catch {
        setJob((j) => (j ? { ...j } : j));
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [job]);

  const model = models.find((m) => m.id === modelId);

  // При смене движка приводим длительность к поддерживаемой (ближайшая / дефолтная).
  useEffect(() => {
    if (!model) return;
    if (!model.durations.includes(durationSec)) {
      const nearest = model.durations.reduce(
        (best, d) => (Math.abs(d - durationSec) < Math.abs(best - durationSec) ? d : best),
        model.durations[0],
      );
      setDurationSec(nearest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);

  const price = model ? model.prices[durationSec] ?? model.prices[model.defaultDuration] ?? null : null;
  const durIdx = model ? Math.max(0, model.durations.indexOf(durationSec)) : 0;
  const noQuota = quota ? quota.limit > 0 && quota.remaining <= 0 : false;
  // Kling/Seedance умеют только оживлять картинку — без стартового кадра не запускаем.
  const needsImage = Boolean(model?.requiresImage) && !sourceMediaId;

  const pickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    setImgBusy(true);
    setError(null);
    try {
      const media = await mediaApi.upload(f);
      setSourceMediaId(media.id);
      if (imgPreview) URL.revokeObjectURL(imgPreview);
      setImgPreview(URL.createObjectURL(f));
    } catch {
      setError('Не удалось загрузить картинку');
    } finally {
      setImgBusy(false);
    }
  };
  const clearImage = () => {
    setSourceMediaId(null);
    if (imgPreview) URL.revokeObjectURL(imgPreview);
    setImgPreview(null);
  };

  const generate = async () => {
    if (!modelId) {
      setError('Выберите движок — вы платите за конкретную модель');
      return;
    }
    if (prompt.trim().length < 3) {
      setError('Опиши сцену — хотя бы пару слов');
      return;
    }
    if (!price) {
      setError('Не удалось рассчитать цену');
      return;
    }
    setBusy(true);
    setError(null);
    let paidRef: string | null = null;
    try {
      const userId = useAuthStore.getState().user?.id;
      if (!userId) throw new Error('Нужно войти');
      // Оплата = подпись ПОЛЬЗОВАТЕЛЯ за конкретную модель (его осознанный выбор).
      // Гарантируем, что pubkey enroll'ен, затем подписываем списание и только
      // после ok запускаем генерацию (aiswipa-signed-debit ТЗ §2).
      await walletApi.enroll(userId);
      const jobId = crypto.randomUUID();
      paidRef = `gen:${jobId}`;
      const pay = await walletApi.debitSigned(userId, {
        amountSub: String(price * SUBUNIT),
        purpose: 'generation:main-ai-video',
        ref: paidRef,
        memo: `ai-video:${modelId}:${durationSec}s`,
      });
      const created = await aiVideoApi.create({
        prompt: prompt.trim(),
        presetId,
        aspectRatio: aspect,
        modelId,
        durationSec,
        sourceMediaId: sourceMediaId ?? undefined,
        jobId,
        txHash: pay.txHash,
      });
      setJob(created);
    } catch (err: any) {
      if (paidRef) await walletApi.cancelUnclaimed(paidRef).catch(() => undefined);
      const msg = err?.response?.data?.message;
      setError(
        msg === 'INSUFFICIENT_FUNDS'
          ? `Недостаточно SWIPA (нужно ${price})`
          : (msg ?? err?.message ?? 'Не удалось запустить генерацию'),
      );
    } finally {
      setBusy(false);
    }
  };

  const publishDraft = async () => {
    if (!job?.draftId) return;
    setPublishing(true);
    try {
      await draftsApi.publish(job.draftId);
      onPublished();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Не удалось опубликовать');
    } finally {
      setPublishing(false);
    }
  };

  const reset = () => {
    setJob(null);
    setError(null);
  };

  // ── Состояние: задача в работе ──────────────────────────────────────────────
  if (job && ACTIVE(job.status)) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <Loader2 size={32} className="animate-spin text-brand-400" />
        <p className="text-sm text-surface-200">Генерация видео…</p>
        <p className="text-xs text-surface-500 max-w-xs">
          Обычно 1–3 минуты. Можно закрыть окно — готовый клип появится в твоих черновиках.
        </p>
      </div>
    );
  }

  // ── Состояние: готово ───────────────────────────────────────────────────────
  if (job && job.status === 'READY') {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <CheckCircle2 size={32} className="text-emerald-400" />
        <div>
          <p className="text-sm text-surface-100 font-medium">Клип готов и сохранён в черновики</p>
          <p className="text-xs text-surface-500 mt-1">Можно опубликовать сразу или отредактировать позже.</p>
        </div>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-2 w-full">
          <button
            onClick={publishDraft}
            disabled={publishing || !job.draftId}
            className="flex-1 py-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 rounded-lg text-white text-sm font-medium"
          >
            {publishing ? 'Публикуем…' : 'Опубликовать'}
          </button>
          <button
            onClick={onPublished}
            className="px-3 py-2 bg-surface-800 hover:bg-surface-700 rounded-lg text-surface-200 text-sm"
          >
            В черновики
          </button>
        </div>
        <button onClick={reset} className="text-xs text-surface-400 hover:text-surface-200">
          Сгенерировать ещё
        </button>
      </div>
    );
  }

  // ── Состояние: провал ───────────────────────────────────────────────────────
  if (job && job.status === 'FAILED') {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <AlertTriangle size={30} className="text-amber-400" />
        <p className="text-sm text-surface-100">Не получилось сгенерировать</p>
        {job.error && <p className="text-xs text-surface-500 max-w-xs">{job.error}</p>}
        {job.refunded && <p className="text-xs text-emerald-400">Монеты SWIPA возвращены</p>}
        <button onClick={reset} className="mt-1 px-4 py-2 bg-brand-600 hover:bg-brand-500 rounded-lg text-white text-sm">
          Попробовать снова
        </button>
      </div>
    );
  }

  // ── Состояние: форма ────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Опиши сцену: «неоновый Токио под дождём, девушка в плаще идёт по улице»"
        maxLength={1000}
        rows={3}
        className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder-surface-500 outline-none focus:border-brand-500 resize-none"
      />

      {/* Соотношение сторон */}
      <div className="flex gap-2">
        {ASPECTS.map((a) => (
          <button
            key={a.value}
            onClick={() => setAspect(a.value)}
            className={clsx(
              'flex-1 px-2 py-1.5 rounded-lg text-xs transition-colors',
              aspect === a.value ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400 hover:bg-surface-700',
            )}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* Выбор движка (модели) */}
      <div>
        <p className="text-xs text-surface-400 mb-2">Движок</p>
        <div className="grid grid-cols-2 gap-2">
          {models.map((m) => {
            const from = Math.min(...Object.values(m.prices));
            return (
              <button
                key={m.id}
                onClick={() => { setModelId(m.id); setDurationSec(m.defaultDuration); }}
                className={clsx(
                  'text-left p-2.5 rounded-lg border transition-colors',
                  modelId === m.id
                    ? 'border-brand-500 bg-brand-600/15'
                    : 'border-surface-700 bg-surface-800 hover:border-surface-600',
                )}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-medium text-surface-100">{m.label}</span>
                  <span className="text-[10px] text-brand-300 whitespace-nowrap">от {from} ₷</span>
                </div>
                <p className="text-[10px] text-surface-500 mt-0.5 leading-tight">{m.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Длительность — слайдер по реальным значениям движка */}
      {model && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-surface-400">Длительность</p>
            <p className="text-xs text-surface-100 font-medium">{durationSec} сек</p>
          </div>
          <input
            type="range"
            min={0}
            max={model.durations.length - 1}
            step={1}
            value={durIdx}
            onChange={(e) => setDurationSec(model.durations[Number(e.target.value)])}
            className="w-full accent-brand-500"
          />
          <div className="flex justify-between text-[10px] text-surface-500">
            <span>{model.durations[0]}с</span>
            <span>{model.durations[model.durations.length - 1]}с</span>
          </div>
        </div>
      )}

      {/* i2v: своя картинка (опционально) */}
      <div>
        {imgPreview ? (
          <div className="relative inline-block">
            <img src={imgPreview} alt="ref" className="h-20 rounded-lg object-cover border border-surface-700" />
            <button onClick={clearImage} className="absolute -top-2 -right-2 bg-surface-900 border border-surface-700 rounded-full p-0.5">
              <X size={12} className="text-surface-300" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={imgBusy}
            className="w-full flex items-center justify-center gap-2 py-2 bg-surface-800 border border-dashed border-surface-600 rounded-lg text-xs text-surface-400 hover:text-surface-200 disabled:opacity-50"
          >
            <ImageIcon size={14} />{' '}
            {imgBusy ? 'Загрузка…' : model?.requiresImage ? 'Загрузите стартовый кадр (обязательно)' : 'Оживить свою картинку (необязательно)'}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
      </div>

      {/* Пресеты камеры (творческий модификатор, на цену не влияет) */}
      <div>
        <p className="text-xs text-surface-400 mb-2">Движение камеры</p>
        <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => setPresetId(p.id)}
              className={clsx(
                'text-left p-2.5 rounded-lg border transition-colors',
                presetId === p.id
                  ? 'border-brand-500 bg-brand-600/15'
                  : 'border-surface-700 bg-surface-800 hover:border-surface-600',
              )}
            >
              <span className="text-xs font-medium text-surface-100">{p.label}</span>
              <p className="text-[10px] text-surface-500 mt-0.5 leading-tight">{p.hint}</p>
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <div className="flex items-center justify-between text-xs text-surface-400">
        <span>
          Стоимость: <span className="text-surface-100 font-medium">{price ?? '—'} SWIPA</span>
          {model && <span className="text-surface-500"> · {model.label} · {durationSec}с</span>}
        </span>
        {quota && quota.limit > 0 && <span>Осталось сегодня: {quota.remaining}</span>}
      </div>

      {needsImage && (
        <p className="text-amber-400 text-xs">
          {model?.label} оживляет вашу картинку — загрузите стартовый кадр выше.
        </p>
      )}

      <button
        onClick={generate}
        disabled={busy || noQuota || needsImage || prompt.trim().length < 3 || !modelId}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 rounded-lg text-white text-sm font-medium transition-colors"
      >
        <Sparkles size={16} />
        {busy
          ? 'Запуск…'
          : noQuota
            ? 'Лимит на сегодня исчерпан'
            : !modelId
              ? 'Выберите движок'
              : needsImage
                ? 'Нужна стартовая картинка'
                : `Сгенерировать${price ? ` · ${price} SWIPA` : ''}`}
      </button>
    </div>
  );
}
